import { getCallDisplayType } from '@utils/call.util'
/* eslint-disable max-len */
import React, { useState, useCallback } from 'react'
import './PhoneSidePanel.css'
import { IconMessageFilled } from '@tabler/icons-react'
import AddContactModal from '@components/layout/Modals/AddContact/AddContactModal.component'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { ICalls, ICallType } from '@dataTypes/calls.type'
import { getFullName } from '@utils/fullname.util'
import ActiveCall from '../ActiveCall/ActiveCall.component'
import { preferredCallMethodOptions } from '@constants/Message'
import { IContact } from '@dataTypes/contact.type'
import {
  SET_ACTIVE_TAB,
  SET_INPUT_NUMBER,
} from '@store/reducers/messageCenter.reducer'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import { setContactPhoneNumber } from '@store/reducers/contact.reducer'
import { useNavigate } from 'react-router-dom'
import { ChatChannel } from '@dataTypes/chat.type'
import { createConversation, updateSelectedMessage } from '@store/actions'

type PhoneSidePanelProps = {
  isHidden: boolean
  toggle: () => void
}

const PhoneSidePanel: React.FC<PhoneSidePanelProps> = ({
  isHidden,
  toggle,
}): React.ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const callHistory = useAppSelector((state) => state.calls.data)
  const contacts = useAppSelector((state) => state.contacts.data)
  const conversations = useAppSelector((state) => state.chat.data)
  const [searchValue, setSearchValue] = useState('')
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [showSearchResults, setShowSearchResults] = useState(false)

  const filteredContacts = searchValue.trim()
    ? contacts.filter((contact) => {
        const nameMatch = getFullName(contact)
          .toLowerCase()
          .includes(searchValue.toLowerCase().trim())
        const phoneMatch = [
          normalizePhoneNumber(contact.cellphone),
          ...(contact.home_phone
            ? [normalizePhoneNumber(contact.home_phone)]
            : []),
          ...(contact.work_phone
            ? [normalizePhoneNumber(contact.work_phone)]
            : []),
        ].some((phone) => phone.includes(searchValue.trim()))
        return nameMatch || phoneMatch
      })
    : []

  const getDateGroupKey = (dateStr: string): string => {
    const parts = dateStr.slice(0, 10).split('-')
    const entryDate = new Date(
      parseInt(parts[0]),
      parseInt(parts[1]) - 1,
      parseInt(parts[2]),
    )

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const oneWeekAgo = new Date(today)
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

    if (entryDate.getTime() === today.getTime()) return 'Today'
    if (entryDate.getTime() === yesterday.getTime()) return 'Yesterday'
    if (entryDate > oneWeekAgo) {
      return entryDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    }
    return entryDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const handleSendMessage = useCallback(
    (contact: IContact): void => {
      dispatch(SET_ACTIVE_TAB('chat'))
      const chat = conversations?.find((c) => {
        const participant = c.chat_participant
        return participant && participant.length == 1 && participant[0].contact
          ? participant[0].contact.contact_id === contact.contact_id
          : false
      })

      if (chat && chat.chat_conversation_id) {
        dispatch(updateSelectedMessage({ id: chat.chat_conversation_id }))
      } else {
        dispatch(
          createConversation({
            conversation_data: {
              channel: ChatChannel.sms,
              chat_message: [],
              chat_participant: [],
            },
            members_data: [
              {
                chat_conversation_id: '',
                phone_number: contact.cellphone,
                contact_id: contact.contact_id ?? undefined,
              },
            ],
          }),
        )
      }
    },
    [dispatch, conversations],
  )

  const getDirectionIcon = (direction: ICallType): string => {
    switch (direction) {
      case 'Outgoing':
        return 'mgc_arrow_right_up_line'
      case 'Incoming':
        return 'mgc_arrow_left_down_line'
      case 'Missed':
        return 'mgc_arrow_to_down_line'
      case 'Decline':
        return 'mgc_corner_down_right_line'
      default:
        return 'mgc_arrow_right_up_line'
    }
  }

  const getCallTime = (key: string, dateTime: string): string => {
    const date = new Date(dateTime)
    if (key === 'Older') {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } else {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    }
  }

  const handleSelectContactNumber = (phone: string): void => {
    setSearchValue('')
    dispatch(SET_INPUT_NUMBER(normalizePhoneNumber(phone)))
  }

  const groupedHistory = callHistory.reduce(
    (acc, entry) => {
      const key = getDateGroupKey(entry.start_date_time)
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(entry)
      return acc
    },
    {} as Record<string, ICalls[]>,
  )

  return (
    <>
      <div
        className={`h-screen flex flex-col bg-white phone-sidepanel ${isHidden ? '' : 'open'}`}
        style={{
          width: isHidden ? '0' : '311px',
          border: isHidden ? 'none' : '1px solid var(--blue-200)',
        }}
      >
        <div
          className="phone-sidepanel-head"
          style={{ display: isHidden ? 'none' : '' }}
        >
          <div className="phone-sidepanel-head-inner">
            <div className="phone-sidepanel-head-top">
              <div className="phone-sidepanel-head-left">
                <div className="phone-sidepanel-title">
                  <span>Phone</span>
                </div>
              </div>
              <div className="phone-sidepanel-collapse">
                <button
                  type="button"
                  className="unstyled-button collapse-icon ti ti-layout-sidebar-right-collapse"
                  aria-label="Collapse phone panel"
                  onClick={toggle}
                />
              </div>
            </div>
            <div className="phone-sidepanel-search">
              <div className="phone-sidepanel-search-container">
                <i className="mgc_search_line"></i>
                <input
                  type="text"
                  className="phone-sidepanel-search-input"
                  placeholder="Search contact"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value)
                    setShowSearchResults(true)
                  }}
                />
                {searchValue.trim().length > 0 && (
                  <button
                    type="button"
                    className="unstyled-button mgc_close_line clear-button"
                    aria-label="Clear search"
                    onClick={() => setSearchValue('')}
                  />
                )}
                <button
                  type="button"
                  className="unstyled-button mgc_user_add_2_fill"
                  aria-label="Add contact"
                  onClick={() => {
                    dispatch(setContactPhoneNumber(searchValue.trim()))
                    setSearchValue('')
                    setShowAddContactModal(true)
                  }}
                />
              </div>
              {searchValue.trim() && showSearchResults && (
                <div className="phone-sidepanel-search-results">
                  {filteredContacts.length === 0 ? (
                    <div className="phone-sidepanel-search-empty">
                      No contacts found
                    </div>
                  ) : (
                    filteredContacts.map((contact) => (
                      <div
                        key={contact.contact_id}
                        className="phone-sidepanel-search-group"
                      >
                        <div className="phone-sidepanel-search-group-title">
                          {getFullName(contact)}
                        </div>
                        {preferredCallMethodOptions.map((label) => {
                          const value = contact[
                            label.id as keyof IContact
                          ] as string
                          const isNameMatch = getFullName(contact)
                            .toLowerCase()
                            .includes(searchValue.toLowerCase().trim())
                          if (
                            value &&
                            (isNameMatch ||
                              normalizePhoneNumber(value).includes(
                                normalizePhoneNumber(searchValue).trim(),
                              ))
                          ) {
                            return (
                              <button
                                type="button"
                                key={`${contact.contact_id}-${label.id}`}
                                className="unstyled-button phone-sidepanel-search-item"
                                onClick={() => handleSelectContactNumber(value)}
                              >
                                <span className="phone-sidepanel-search-number-row">
                                  <span className="phone-sidepanel-search-number">
                                    {formatPhoneNumber(value)}
                                  </span>
                                  {label.id ===
                                    contact.preferred_call_method && (
                                    <span className="phone-sidepanel-search-pill">
                                      Preferred
                                    </span>
                                  )}
                                </span>
                                <span className="phone-sidepanel-search-label">
                                  {label.text}
                                </span>
                              </button>
                            )
                          } else return null
                        })}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="phone-sidepanel-body"
          style={{ display: isHidden ? 'none' : '' }}
        >
          <div className="phone-sidepanel-call-history">
            <div className="phone-sidepanel-call-history-title">
              Call history
            </div>
            {Object.keys(groupedHistory).length === 0 && searchValue.trim() ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'var(--gray-500)',
                  fontSize: '14px',
                }}
              >
                No contacts found matching &quot;{searchValue}&quot;
              </div>
            ) : (
              Object.entries(groupedHistory).map(([date, entries]) => (
                <div key={date} className="phone-sidepanel-call-history-group">
                  <div className="phone-sidepanel-call-history-date">
                    {date}
                  </div>
                  {entries.map((entry) => (
                    <div
                      key={entry.call_id}
                      className="phone-sidepanel-call-history-item"
                      onClick={() => {
                        dispatch(SET_INPUT_NUMBER(entry.caller_number))
                      }}
                    >
                      <div className="phone-sidepanel-call-history-item-left">
                        <div className="phone-sidepanel-call-history-item-name">
                          {entry.contact
                            ? getFullName(entry.contact)
                            : 'Unknown caller'}
                        </div>
                        <div className="phone-sidepanel-call-history-item-details">
                          <span
                            className={getDirectionIcon(
                              getCallDisplayType(entry),
                            )}
                          ></span>
                          <span className="phone-sidepanel-call-history-item-phone">
                            {formatPhoneNumber(
                              ['Outgoing', 'Decline'].includes(entry.call_type)
                                ? entry.receiver_number
                                : entry.caller_number,
                            )}
                          </span>
                          <span className="phone-sidepanel-call-history-item-time">
                            {getCallTime(date, entry.start_date_time)}
                          </span>
                        </div>
                      </div>
                      <div className="phone-sidepanel-call-history-item-actions">
                        <div
                          className="phone-sidepanel-call-history-action-button phone-sidepanel-call-history-action-button-phone"
                          onClick={(e) => {
                            e.stopPropagation()
                            dispatch(
                              SET_INPUT_NUMBER(
                                ['Outgoing', 'Decline'].includes(
                                  entry.call_type,
                                )
                                  ? entry.receiver_number
                                  : entry.caller_number,
                              ),
                            )
                          }}
                        >
                          <i className="mgc_phone_fill"></i>
                        </div>
                        <div
                          className="phone-sidepanel-call-history-action-button phone-sidepanel-call-history-action-button-message"
                          onClick={(e) => {
                            e.stopPropagation()
                            const _callContact = contacts.find(
                              (c) => c.contact_id === entry.contact?.contact_id,
                            )
                            const _contact: IContact = _callContact ?? {
                              clinic_id: '',
                              title: 'Mr.',
                              first_name: '',
                              last_name: '',
                              address: '',
                              city: '',
                              state: '',
                              zip: '',
                              cellphone: ['Outgoing', 'Decline'].includes(
                                entry.call_type,
                              )
                                ? normalizePhoneNumber(entry.receiver_number)
                                : normalizePhoneNumber(entry.caller_number),
                              email: '',
                            }

                            handleSendMessage(_contact)
                            navigate('/message')
                          }}
                        >
                          <IconMessageFilled size={18} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        <ActiveCall show={true} isModal={false} />

        {showAddContactModal && (
          <AddContactModal
            show={showAddContactModal}
            onHide={() => setShowAddContactModal(false)}
            fetchCalls
          />
        )}
      </div>
    </>
  )
}

export default PhoneSidePanel
